import { computed, reactive, unref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';

// En la práctica los agentes usan la nota privada de la conversación y la
// nota de contacto para lo mismo. Aquí se leen juntas: las de contacto
// vienen del store Vuex y las privadas de un endpoint propio, que se cachea
// por contacto para que el panel lateral, la pestaña de notas y los
// contadores compartan una sola petición.
const cache = reactive({});
const inFlight = new Map();

const mapConversationNote = note => ({
  key: `message-${note.id}`,
  id: note.id,
  content: note.content,
  createdAt: note.created_at,
  user: note.user,
  conversationDisplayId: note.conversation_display_id,
  isContactNote: false,
});

export const fetchConversationNotes = (contactId, { force = false } = {}) => {
  if (!contactId) return Promise.resolve([]);

  const key = String(contactId);
  if (!force && cache[key]) return Promise.resolve(cache[key]);
  if (inFlight.has(key)) return inFlight.get(key);

  const request = ContactAPI.getConversationNotes(contactId)
    .then(({ data }) => (data?.payload || []).map(mapConversationNote))
    .catch(() => [])
    .then(notes => {
      cache[key] = notes;
      inFlight.delete(key);
      return notes;
    });

  inFlight.set(key, request);
  return request;
};

export function useContactNotes(contactId) {
  const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
  const uiFlags = useMapGetter('contactNotes/getUIFlags');

  const id = computed(() => unref(contactId));

  const contactNotes = computed(() =>
    id.value ? notesByContact.value(id.value) || [] : []
  );

  const conversationNotes = computed(
    () => (id.value ? cache[String(id.value)] : null) || []
  );

  const allNotes = computed(() =>
    [
      ...contactNotes.value.map(note => ({
        ...note,
        key: `note-${note.id}`,
        isContactNote: true,
      })),
      ...conversationNotes.value,
    ].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  );

  watch(
    id,
    value => {
      // Se refresca al abrir el contacto (las peticiones simultáneas de
      // varios consumidores comparten una sola). El valor cacheado se sigue
      // pintando mientras llega el nuevo, así que no parpadea.
      if (value) fetchConversationNotes(value, { force: true });
    },
    { immediate: true }
  );

  return {
    contactNotes,
    conversationNotes,
    allNotes,
    isFetchingContactNotes: computed(() => uiFlags.value.isFetching),
    isCreatingContactNote: computed(() => uiFlags.value.isCreating),
  };
}
